export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid="privacy-policy-page">
      <div className="eyebrow mb-4">Legal</div>
      <h1 className="font-serif text-5xl md:text-6xl text-forest-900 leading-tight">Privacy Policy</h1>
      <div className="article-body mt-10">
        <p className="lead">
          At Githy.com, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and disclose information when you access or use our website.
        </p>
        <p>
          Githy.com publishes informational content related to travel, finance, technology, ecommerce, sports, and related topics. The website may also display advertisements, sponsored content, and affiliate links through affiliate marketing programs.
        </p>
        <p>
          By using this website, you agree to the practices described in this Privacy Policy.
        </p>

        <h2>1. Information We Collect</h2>
        <h3>a) Personal Information</h3>
        <p>We may collect personal information that you voluntarily provide, including:</p>
        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Contact information</li>
          <li>Information submitted through forms, comments, or subscriptions</li>
        </ul>

        <h3>b) Automatically Collected Information</h3>
        <p>When you visit the website, we may automatically collect certain information, such as:</p>
        <ul>
          <li>IP address</li>
          <li>Browser type</li>
          <li>Device information</li>
          <li>Operating system</li>
          <li>Internet service provider</li>
          <li>Pages visited</li>
          <li>Date and time of visits</li>
          <li>Referral URLs</li>
          <li>User interaction data</li>
          <li>Cookie identifiers</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We may use the information collected to:</p>
        <ul>
          <li>Operate and maintain the website</li>
          <li>Improve website functionality and user experience</li>
          <li>Analyze traffic and visitor behavior</li>
          <li>Respond to inquiries and comments</li>
          <li>Send newsletters or updates, only if subscribed</li>
          <li>Prevent fraudulent or unauthorized activities</li>
          <li>Display relevant advertisements</li>
          <li>Track affiliate referrals and commissions</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h2>3. Cookies and Tracking Technologies</h2>
        <p>
          Githy.com uses cookies, pixels, tags, and similar tracking technologies to improve user experience and support analytics and advertising functions.
        </p>
        <p>Cookies may be used for:</p>
        <ul>
          <li>Website functionality</li>
          <li>Performance and analytics</li>
          <li>Personalized advertising</li>
          <li>Affiliate link tracking</li>
          <li>Ad measurement and reporting</li>
        </ul>
        <p>
          You can control or disable cookies through your browser settings. However, disabling cookies may affect certain website features.
        </p>

        <h2>4. Affiliate Disclosure</h2>
        <p>
          Githy.com participates in affiliate marketing programs and may earn commissions when users click affiliate links and complete purchases or actions through those links.
        </p>
        <p>
          These affiliate programs may use cookies and tracking technologies to attribute commissions and monitor referrals.
        </p>
        <p>
          Clicking affiliate links does not typically increase the cost to users. We strive to provide honest opinions, recommendations, and informational content regardless of affiliate relationships.
        </p>

        <h2>5. Third-Party Advertising</h2>
        <p>We may display third-party advertisements through advertising partners such as:</p>
        <ul>
          <li>Google Ads</li>
          <li>Affiliate advertising platforms</li>
          <li>Sponsored content providers</li>
          <li>Retargeting and display advertising networks</li>
        </ul>
        <p>
          Third-party vendors may use cookies, web beacons, and similar technologies to serve ads based on your previous visits to this website and other websites.
        </p>
        <p>
          Learn more about Google advertising policies here: <a href="https://support.google.com/adspolicy" target="_blank" rel="noreferrer">Google Ads Policies</a>
        </p>

        <h2>6. Analytics Services</h2>
        <p>We may use third-party analytics services to monitor website usage and improve performance, including:</p>
        <ul>
          <li>Google Analytics</li>
          <li>Search Console tools</li>
          <li>Heatmap and visitor analytics platforms</li>
        </ul>
        <p>These services may collect anonymized traffic and behavioral data.</p>
        <p>
          Learn more about Google Analytics practices here: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noreferrer">Google Analytics Privacy Information</a>
        </p>

        <h2>7. Third-Party Links</h2>
        <p>
          Our website may contain links to third-party websites, services, products, or applications.
        </p>
        <p>
          We are not responsible for the privacy practices, content, or policies of external websites. Users should review the privacy policies of those third-party websites independently.
        </p>

        <h2>8. Data Sharing and Disclosure</h2>
        <p>We do not sell personal information to third parties.</p>
        <p>However, we may share information with:</p>
        <ul>
          <li>Affiliate marketing platforms</li>
          <li>Advertising partners</li>
          <li>Analytics providers</li>
          <li>Hosting and technical service providers</li>
          <li>Legal authorities when required by applicable law</li>
          <li>Fraud prevention and security providers</li>
        </ul>

        <h2>9. Data Security</h2>
        <p>
          We implement commercially reasonable security measures to help protect your information. However, no electronic transmission or storage method is completely secure, and we cannot guarantee absolute security.
        </p>

        <h2>10. GDPR and Privacy Rights</h2>
        <p>
          Depending on your location, including the European Economic Area, United Kingdom, or similar jurisdictions, you may have rights regarding your personal data, including:
        </p>
        <ul>
          <li>Right to access</li>
          <li>Right to correction</li>
          <li>Right to deletion</li>
          <li>Right to object to processing</li>
          <li>Right to withdraw consent</li>
          <li>Right to data portability</li>
        </ul>
        <p>To exercise these rights, contact us using the details on the contact page.</p>

        <h2>11. CCPA Privacy Rights</h2>
        <p>
          If applicable, California residents may have rights under the California Consumer Privacy Act, including the right to know what personal information is collected, request deletion of personal information, and request disclosure of data-sharing practices.
        </p>
        <p>We do not sell personal information as defined under the CCPA.</p>

        <h2>12. Children&apos;s Privacy</h2>
        <p>
          Githy.com is not directed toward children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has submitted personal information, please contact us for removal.
        </p>

        <h2>13. Changes to This Privacy Policy</h2>
        <p>
          We reserve the right to update or modify this Privacy Policy at any time. Changes will become effective immediately upon posting on this page. Continued use of the website constitutes acceptance of the updated policy.
        </p>
      </div>
    </div>
  );
}
